import { useState } from "react";
import api, { formatApiError } from "../api";
import { useApp } from "../context/AppContext";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ServiceCTA } from "../components/LeadCta";

const input = "w-full h-11 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

export default function RentAgreement() {
  const { user, lang } = useApp();
  const [form, setForm] = useState({ duration_months: 11, notice_period_days: 30 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please login to generate an agreement"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/rent-agreements", {
        ...form, monthly_rent: Number(form.monthly_rent), deposit: Number(form.deposit || 0),
        duration_months: Number(form.duration_months), notice_period_days: Number(form.notice_period_days),
      });
      setResult(data);
      toast.success("Agreement created");
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  const downloadPdf = async (id, no) => {
    const r = await api.get(`/rent-agreements/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `RentAgreement_${no}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const F = [
    ["landlord_name", "Landlord Name / બાડેદારનું નામ", true],
    ["landlord_address", "Landlord Address", false],
    ["tenant_name", "Tenant Name / ભાડૂતનું નામ", true],
    ["tenant_address", "Tenant Address", false],
    ["property_address", "Property Address / મિલ્કતનું સરનામું", true],
    ["monthly_rent", "Monthly Rent (₹)", true],
    ["deposit", "Security Deposit (₹)", false],
    ["duration_months", "Duration (months)", true],
    ["notice_period_days", "Notice Period (days)", true],
    ["start_date", "Start Date (YYYY-MM-DD)", false],
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="rent-agreement-page">
      <Seo title="Rent Agreement Gujarat" description="Create a professional rent agreement (Gujarati + English) and download a printable PDF." />
      <h1 className="font-display font-bold text-3xl md:text-4xl">Rent Agreement / ભાડા કરાર</h1>
      <p className="text-muted-foreground mt-2">Fill the details, generate a professional printable PDF in Gujarati + English.</p>

      {!result ? (
        <form onSubmit={submit} className="mt-6 bg-white border border-border rounded-xl p-6 grid sm:grid-cols-2 gap-4" data-testid="rent-form">
          {F.map(([k, label, req]) => (
            <div key={k} className={k.includes("address") ? "sm:col-span-2" : ""}>
              <label className="text-xs font-semibold text-muted-foreground">{label}</label>
              <input className={input} required={req} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`rent-${k}`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-muted-foreground">Other clauses (optional)</label>
            <textarea className={`${input} h-20 py-2`} value={form.clauses || ""} onChange={(e) => setForm({ ...form, clauses: e.target.value })} data-testid="rent-clauses" />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" className="bg-primary h-11 px-8" disabled={loading} data-testid="rent-submit-btn">
              {loading ? "Generating..." : "Generate Agreement"}
            </Button>
          </div>
        </form>
      ) : (
        <div className="mt-6 bg-white border border-border rounded-xl p-8 text-center fade-up" data-testid="rent-result">
          <h2 className="font-display font-bold text-xl">Agreement ready</h2>
          <p className="font-mono text-primary font-bold text-lg mt-1" data-testid="rent-agreement-no">{result.agreement_no}</p>
          <div className="mt-5 flex justify-center gap-3">
            <Button onClick={() => downloadPdf(result.id, result.agreement_no)} className="bg-secondary" data-testid="rent-download-btn">Download PDF</Button>
            <Button variant="outline" onClick={() => setResult(null)} data-testid="rent-new-btn">Create another</Button>
          </div>
          <p className="text-xs text-muted-foreground mt-4">For legal validity, get the agreement notarized/registered and pay applicable stamp duty.</p>
        </div>
      )}
      <ServiceCTA service="Rent Agreement"
        title={lang === "gu" ? "ભાડા કરાર ઈ-સ્ટેમ્પ + નોટરી સાથે તૈયાર કરાવવો છે?" : "Want this rent agreement e-stamped & notarised?"}
        subtitle={lang === "gu" ? "રાજકોટ અને ગુજરાતમાં ગુજરાતી/English ભાડા કરાર ડ્રાફ્ટિંગ, ઈ-સ્ટેમ્પિંગ અને નોટરી સહાય. WhatsApp કરો અથવા ફોર્મ ભરો." : "Gujarati/English rent agreement drafting, e-stamping and notary assistance in Rajkot & across Gujarat. WhatsApp us or fill the form."} />
      <p className="text-sm text-muted-foreground mt-6">
        Read: <a href="/blog/rent-agreement-rajkot-e-stamping-typing-notary-service" className="text-primary font-semibold">Rent Agreement in Rajkot — e-stamping, typing &amp; notary</a> ·{" "}
        <a href="/e-stamping" className="text-primary font-semibold">E-Stamping</a> · <a href="/blog/garvi-2-guide-gujarat" className="text-primary font-semibold">Garvi 2.0 registration guide</a>
      </p>
    </div>
  );
}
